import { Injectable, NgZone } from '@angular/core'
import { comboFromEvent } from './key-combo'
import { ShortcutRegistryService } from './shortcut-registry.service'
import { pickCandidate } from './shortcut-resolver'

/**
 * The app's only global keydown listener for shortcuts. Runs outside Angular
 * and re-enters the zone only when a shortcut actually fires.
 */
@Injectable({ providedIn: 'root' })
export class ShortcutDispatcherService {
    /** While > 0 nothing dispatches (e.g. the recorder is capturing a combo). */
    private pauseCount = 0

    private started = false

    constructor (private zone: NgZone, private registry: ShortcutRegistryService) { }

    start () {
        if (this.started) return
        this.started = true
        this.zone.runOutsideAngular(() => {
            document.addEventListener('keydown', this.onKeyDown, { capture: true })
        })
    }

    /** Suspends dispatching. Returns the resume function. */
    pause (): () => void {
        this.pauseCount++
        let resumed = false
        return () => {
            if (resumed) return
            resumed = true
            this.pauseCount--
        }
    }

    private onKeyDown = (e: KeyboardEvent) => {
        if (this.pauseCount > 0 || e.repeat || e.isComposing || e.defaultPrevented) return
        const combo = comboFromEvent(e)
        if (!combo) return

        // A Bootstrap modal that didn't register a modal scope still blocks screen/global shortcuts
        const unscopedModalOpen = document.body.classList.contains('modal-open') && this.registry.modals().length === 0
        const cmd = pickCandidate(this.registry.keyMap().get(combo), isTypingTarget(e), unscopedModalOpen)
        if (!cmd) return

        e.preventDefault()
        e.stopPropagation()
        this.zone.run(() => cmd.run(e))
    }
}

function isTypingTarget (e: KeyboardEvent): boolean {
    const el = (e.composedPath?.()[0] ?? e.target) as HTMLElement | null
    if (!el || !(el instanceof HTMLElement)) return false
    if (el.isContentEditable) return true
    const tag = el.tagName
    if (tag === 'TEXTAREA' || tag === 'SELECT') return true
    if (tag !== 'INPUT') return false
    const type = (el as HTMLInputElement).type
    return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file'].includes(type)
}
